import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DeleteMessageModal from './DeleteMessageModal';
import ForwardedFromQuote from './ForwardedFromQuote';
import MarkdownRenderer from './MarkdownRenderer';
import MessageAttachments from './MessageAttachments';
import MessageHoverToolbar from './MessageHoverToolbar';
import ReactionBar from './ReactionBar';
import ReactionPicker from './ReactionPicker';
import WidgetRenderer from './widgets/WidgetRenderer';
import { parseReactionCounts } from './reactionCounts';
import { toMillis } from './time';
import { copyMessageDeepLink } from './messageDeepLink';
import { useReactionToggle } from './useReactionToggle';
import './MessageBubble.css';

const LOADING_WORDS = ['tinkering...', 'processing...', 'flabbergasting...'];
const HIDDEN_CONFIRM_OUTCOME_WIDGET_TYPES = new Set(['patient_result', 'order_result']);
const SUPPRESSIBLE_CONFIRM_OUTCOME_WIDGET_TYPES = new Set([
  'status',
  'order_bulk_result',
  'patient_result',
  'order_result',
  'order_list',
  'patient_duplicate',
  'order_duplicate'
]);

/**
 * Format a timestamp into a relative time string (e.g. "2m ago", "1h ago").
 */
function relativeTime(timestamp) {
  const now = Date.now();
  const then = toMillis(timestamp);
  if (!then) return '';
  const diffMs = now - then;
  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(then).toLocaleDateString();
}

/**
 * Get initials from a display name (first letter of first two words).
 */
function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return parts[0].slice(0, 2).toUpperCase();
}

/**
 * MessageBubble — renders a single chat message with avatar, content,
 * reactions, thread info, and hover actions.
 */
function MessageBubble({
  message,
  currentUserId,
  // KAP-313 — sender always sees Delete; an admin viewer also sees it.
  // Resolved by the parent (UnifiedChatPanel) from the cached profile so
  // the bubble itself stays presentational.
  isAdmin,
  hasAssistantFollowUp,
  bulkFollowUp,
  suppressProgressAfterConfirm,
  suppressOutcomeAfterConfirm,
  onConfirmationSubmitted,
  onOpenThread,
  onAddReaction,
  onToggleReaction,
  onEditMessage,
  onDeleteMessage,
  // KAP-311 — message-interaction callbacks. Each is null until its owning
  // commit wires the mutation in UnifiedChatPanel. The toolbar hides the
  // corresponding kebab item when the callback is missing.
  onForwardMessage,
  onPinMessage,
  onUnpinMessage,
  onBookmarkMessage,
  onUnbookmarkMessage,
  // KAP-311.3 — bookmark state for THIS message. UnifiedChatPanel projects
  // listBookmarks → Set<messageId> once at mount and passes the boolean
  // down so each bubble can label its kebab item correctly.
  isBookmarked = false,
  // KAP-310 — user's persisted quick-react trio (or default fallback).
  quickReactTokens
}) {
  const [showActions, setShowActions] = useState(false);
  const [hasFocusWithin, setHasFocusWithin] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const deleteReturnFocusRef = useRef(null);
  const [linkCopiedAt, setLinkCopiedAt] = useState(0);
  const [loadingWordIndex, setLoadingWordIndex] = useState(0);
  const wrapperRef = useRef(null);
  // Inline edit state. Activated by the Edit hover action; submitting calls
  // back to the parent's onEditMessage which dispatches the GraphQL mutation.
  // Owner-only — backend enforces sender === caller, but we also hide the
  // affordance for non-owners up front to avoid an obvious UI footgun.
  const [isEditing, setIsEditing] = useState(false);
  const [draftContent, setDraftContent] = useState('');
  const [editing, setEditing] = useState(false);

  const isAI = message.role === 'assistant' || message.role === 'system';
  const senderName = message.senderDisplayName || message.senderId || 'Unknown';
  const isOwn = !isAI && currentUserId && message.senderId === currentUserId;
  // KAP-313 — soft-delete state. When set, the bubble swaps content for a
  // "This message was deleted" placeholder, suppresses reactions, and hides
  // the thread badge per KAP-313 acceptance criteria.
  const isDeleted = !!message.deletedAt;
  // KAP-311.1 — forwarded snapshot. A forwarded message can have empty
  // `content` if the forwarder added no note — the quote block alone is the
  // payload, which is why the empty-content guard below has to exempt it.
  const isForwarded = !!message.forwardedFrom;
  // Slack-parity "Also send to channel" — a top-level channel message
  // echoed from a thread reply. Renders a quote header pointing back to
  // the thread parent (clickable to open the thread side panel).
  const isThreadReplyEcho = !!message.repliedToThreadParentId;
  // Thread reply that the user opted to echo into the parent channel.
  // Renders a small "Also sent to the channel" indicator above the body.
  const isAlsoSentToChannel = !!message.alsoSentToChannel;
  // KAP-311.2 — pinned state arrives via the cached message field, which
  // both the initial query and the onNewMessage subscription deliver.
  const isPinned = !!message.pinnedAt;
  const canEdit = isOwn && !!onEditMessage && message.isComplete !== false && !isDeleted;
  // Delete available to sender or admin viewer; AI messages cannot be
  // deleted (no senderId match).
  const canDelete = !isAI && !isDeleted && !!onDeleteMessage && (isOwn || isAdmin === true);
  // KAP-311.1 — forward any non-deleted, non-AI message the viewer can see.
  const canForward = !isAI && !isDeleted && !!onForwardMessage;
  // KAP-311.2 — any conversation member can pin (handler-enforced membership);
  // AI messages and deleted rows are excluded for UX clarity.
  const canPin = !isAI && !isDeleted && !!onPinMessage && !!onUnpinMessage;
  // KAP-311.3 — bookmarking is per-user; same eligibility as pin.
  const canBookmark = !isAI && !isDeleted && !!onBookmarkMessage && !!onUnbookmarkMessage;
  // editedAt is set by the backend's handleEditMessage on every successful
  // edit. Render a small "(edited)" suffix only when it differs meaningfully
  // from createdAt — within 1s of create we assume any edit was an
  // anti-flicker write rather than a real human edit.
  const editedAtMs = toMillis(message.editedAt);
  const createdAtMs = toMillis(message.createdAt || message.timestamp);
  const isEdited = !!(editedAtMs && createdAtMs && editedAtMs - createdAtMs > 1000);
  const initials = getInitials(senderName);
  const hasWidget = !!message.widgetType;
  const hasTextContent = typeof message.content === 'string'
    ? message.content.trim().length > 0
    : !!message.content;
  // Attachment-only sends (image or PDF, no caption) are now allowed end-to-end
  // — the bubble must stay visible even when content is empty.
  const hasAttachments = Array.isArray(message.attachments) && message.attachments.length > 0;
  const showLoadingState = message.isComplete === false && !hasTextContent && !hasWidget && !hasAttachments;
  const parsedReactionCounts = useMemo(
    () => parseReactionCounts(message.reactionCounts),
    [message.reactionCounts]
  );
  const [reactionCounts, setReactionCounts] = useState(parsedReactionCounts);
  const threadReplyCount = message.threadReplyCount || 0;

  useEffect(() => {
    setReactionCounts(parsedReactionCounts);
  }, [parsedReactionCounts]);

  useEffect(() => {
    if (!showLoadingState) {
      setLoadingWordIndex(0);
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setLoadingWordIndex((current) => (current + 1) % LOADING_WORDS.length);
    }, 3000);
    return () => window.clearInterval(intervalId);
  }, [showLoadingState]);

  const incrementReactionCount = (emojiToken) => {
    setReactionCounts((prev) => {
      const safePrev = parseReactionCounts(prev) || {};
      const next = { ...safePrev };
      next[emojiToken] = (next[emojiToken] || 0) + 1;
      return next;
    });
  };

  // Quick-react chip toggle. Shared with `ReactionBar`'s pill toggle via
  // the `useReactionToggle` hook so the chip and the pill cannot drift.
  const handleQuickReact = useReactionToggle({
    messageId: message.id,
    conversationId: message.conversationId,
    currentUserId,
    reactionCounts,
    setReactionCounts
  });

  // KAP-309 — focus-within parity with hover. We use onFocus/onBlur on the
  // wrapper and check `relatedTarget` to detect focus that exited the
  // subtree. CSS :focus-within would also work but the React event lets
  // us coalesce hover + focus into a single show/hide flag.
  const handleFocusEnter = useCallback(() => {
    setHasFocusWithin(true);
  }, []);

  const handleFocusLeave = useCallback((event) => {
    if (!wrapperRef.current?.contains(event.relatedTarget)) {
      setHasFocusWithin(false);
    }
  }, []);

  const dismissToolbar = useCallback(() => {
    if (wrapperRef.current && typeof wrapperRef.current.focus === 'function') {
      wrapperRef.current.focus();
    }
    setShowPicker(false);
  }, []);

  const handleOpenPicker = useCallback(() => {
    setShowPicker(true);
  }, []);

  const handleClosePicker = useCallback(() => {
    setShowPicker(false);
  }, []);

  const editActionsRef = useRef(null);

  const handleStartEdit = useCallback(() => {
    setDraftContent(message.content || '');
    setIsEditing(true);
  }, [message.content]);

  // When edit mode opens, the bubble grows from a single text row to a
  // textarea + Cancel/Save action row. If the original bubble was near
  // the bottom of the message list (the last few messages), the new
  // action row sits below the visible scroll area and the user has to
  // hand-scroll to reach Save. We target the actions row directly with
  // `block: 'nearest'` so the scroll only fires when the buttons are
  // genuinely off-screen — already-visible buttons see no jump.
  useEffect(() => {
    if (!isEditing) return;
    const id = requestAnimationFrame(() => {
      const el = editActionsRef.current;
      if (el && typeof el.scrollIntoView === 'function') {
        try {
          el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        } catch (_err) {
          el.scrollIntoView(false);
        }
      }
    });
    return () => cancelAnimationFrame(id);
  }, [isEditing]);

  // Opens the Slack-parity confirmation modal. The toolbar fires this
  // when the user clicks the inline kebab → Delete. The actual mutation
  // lives in `confirmDelete` below and is invoked from the modal's
  // primary action.
  const handleDelete = useCallback(() => {
    if (typeof onDeleteMessage !== 'function') return;
    // Capture the active element so we can return focus to the kebab
    // (or whichever trigger opened the modal) on close.
    if (typeof document !== 'undefined' && document.activeElement) {
      deleteReturnFocusRef.current = document.activeElement;
    }
    setShowDeleteConfirm(true);
  }, [onDeleteMessage]);

  const confirmDelete = useCallback(async () => {
    if (typeof onDeleteMessage !== 'function') return;
    await onDeleteMessage(message.id, message.conversationId);
  }, [onDeleteMessage, message.id, message.conversationId]);

  const cancelDelete = useCallback(() => {
    setShowDeleteConfirm(false);
  }, []);

  const handleForward = useCallback(() => {
    if (typeof onForwardMessage !== 'function') return;
    onForwardMessage(message);
  }, [onForwardMessage, message]);

  const handlePin = useCallback(async () => {
    if (typeof onPinMessage !== 'function') return;
    try {
      await onPinMessage(message.id, message.conversationId);
    } catch (err) {
      // eslint-disable-next-line no-alert
      if (typeof window !== 'undefined' && typeof window.alert === 'function') {
        window.alert(`Failed to pin message: ${err?.message || 'unknown error'}`);
      }
      // eslint-disable-next-line no-console
      console.error('Failed to pin message:', err);
    }
  }, [onPinMessage, message.id, message.conversationId]);

  const handleUnpin = useCallback(async () => {
    if (typeof onUnpinMessage !== 'function') return;
    try {
      await onUnpinMessage(message.id, message.conversationId);
    } catch (err) {
      // eslint-disable-next-line no-alert
      if (typeof window !== 'undefined' && typeof window.alert === 'function') {
        window.alert(`Failed to unpin message: ${err?.message || 'unknown error'}`);
      }
      // eslint-disable-next-line no-console
      console.error('Failed to unpin message:', err);
    }
  }, [onUnpinMessage, message.id, message.conversationId]);

  const handleBookmark = useCallback(async () => {
    if (typeof onBookmarkMessage !== 'function') return;
    try {
      await onBookmarkMessage(message.id, message.conversationId);
    } catch (err) {
      // eslint-disable-next-line no-alert
      if (typeof window !== 'undefined' && typeof window.alert === 'function') {
        window.alert(`Failed to bookmark message: ${err?.message || 'unknown error'}`);
      }
      // eslint-disable-next-line no-console
      console.error('Failed to bookmark message:', err);
    }
  }, [onBookmarkMessage, message.id, message.conversationId]);

  const handleUnbookmark = useCallback(async () => {
    if (typeof onUnbookmarkMessage !== 'function') return;
    try {
      await onUnbookmarkMessage(message.id, message.conversationId);
    } catch (err) {
      // eslint-disable-next-line no-alert
      if (typeof window !== 'undefined' && typeof window.alert === 'function') {
        window.alert(`Failed to remove bookmark: ${err?.message || 'unknown error'}`);
      }
      // eslint-disable-next-line no-console
      console.error('Failed to remove bookmark:', err);
    }
  }, [onUnbookmarkMessage, message.id, message.conversationId]);

  const handleCopyLink = useCallback(async () => {
    const result = await copyMessageDeepLink({
      conversationId: message.conversationId,
      messageId: message.id
    });
    if (result.ok) {
      setLinkCopiedAt(Date.now());
    } else if (result.link && typeof window !== 'undefined' && typeof window.prompt === 'function') {
      // Clipboard unavailable (older browsers, insecure contexts) — surface
      // the URL so the user can copy it manually rather than losing the
      // intent silently. Test envs without prompt() simply no-op.
      try {
        window.prompt('Copy this link', result.link);
      } catch (_) {
        /* jsdom prompt is unimplemented — swallow */
      }
    }
  }, [message.conversationId, message.id]);

  // Auto-clear the "Link copied" toast after 2s.
  useEffect(() => {
    if (!linkCopiedAt) return undefined;
    const id = window.setTimeout(() => setLinkCopiedAt(0), 2000);
    return () => window.clearTimeout(id);
  }, [linkCopiedAt]);

  // KAP-313 fix-forward: a soft-deleted message has blank `content` (set by
  // handleDeleteMessage), so `hasTextContent` is false and the empty-bubble
  // guard below would hide the entire bubble — losing the "This message was
  // deleted" placeholder and breaking the KAP-313 acceptance criterion
  // ("message renders as 'This message was deleted' for everyone"). Always
  // let deleted rows through.
  //
  // KAP-311.1 extension: a forwarded message with no caller note also has
  // blank `content`; the quote block IS the payload. Exempt forwarded rows
  // from the empty-bubble guard for the same reason.
  if (!hasWidget && message.isComplete === true && !hasTextContent && !hasAttachments && !isDeleted && !isForwarded) {
    return null;
  }

  if (message.widgetType === 'progress' && suppressProgressAfterConfirm) {
    return null;
  }

  // Single-card confirm UX: never render dedicated patient/order completion cards.
  // We still keep these rows in message state so confirmation widgets can reconcile to "Complete".
  if (HIDDEN_CONFIRM_OUTCOME_WIDGET_TYPES.has(message.widgetType)) {
    return null;
  }

  if (suppressOutcomeAfterConfirm && SUPPRESSIBLE_CONFIRM_OUTCOME_WIDGET_TYPES.has(message.widgetType)) {
    return null;
  }

  const toolbarVisible = showActions || hasFocusWithin;

  return (
    <div
      ref={wrapperRef}
      className={`message-bubble ${isAI ? 'message-bubble-ai' : 'message-bubble-user'}`}
      data-message-id={message.id}
      tabIndex={0}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
      onFocus={handleFocusEnter}
      onBlur={handleFocusLeave}
    >
      <div className={`message-bubble-avatar ${isAI ? 'avatar-ai' : 'avatar-human'}`}>
        {isAI ? 'K' : initials}
      </div>

      <div className="message-bubble-body">
        <div className="message-bubble-header">
          <span className={`message-bubble-sender ${isAI ? 'sender-ai' : ''}`}>
            {senderName}
          </span>
          {isAI && <span className="msg-ai-badge">AI</span>}
          <span className="message-bubble-time">{relativeTime(message.createdAt || message.timestamp)}</span>
          {isPinned && !isDeleted && (
            <span
              className="message-bubble-pin-badge"
              title="Pinned to this conversation"
              aria-label="Pinned"
            >
              <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor" aria-hidden="true">
                <path d="M16 9V4l1-1V2H7v1l1 1v5L4 14v2h6v6l1 1 1-1v-6h6v-2l-4-5z" />
              </svg>
              <span className="message-bubble-pin-label">Pinned</span>
            </span>
          )}
        </div>

        {isForwarded && !isDeleted && (
          <ForwardedFromQuote forwardedFrom={message.forwardedFrom} />
        )}

        {isThreadReplyEcho && !isDeleted && (
          <button
            type="button"
            className="message-bubble-thread-echo-quote"
            onClick={() => onOpenThread && onOpenThread(message.repliedToThreadParentId)}
            title="Open thread"
          >
            <span className="message-bubble-thread-echo-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 17 4 12 9 7" />
                <path d="M20 18v-2a4 4 0 0 0-4-4H4" />
              </svg>
            </span>
            <span className="message-bubble-thread-echo-label">replied to a thread:</span>
            {message.repliedToThreadParentPreview && (
              <span className="message-bubble-thread-echo-preview">
                {message.repliedToThreadParentPreview}
              </span>
            )}
          </button>
        )}

        {isAlsoSentToChannel && !isDeleted && (
          <div className="message-bubble-also-sent-notice" aria-label="Also sent to the channel">
            <span aria-hidden="true">
              <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 17 20 12 15 7" />
                <path d="M4 18v-2a4 4 0 0 1 4-4h12" />
              </svg>
            </span>
            <span>Also sent to the channel</span>
          </div>
        )}

        <div className="message-bubble-content">
          {showLoadingState ? (
            <div className="typing-indicator">
              <span></span>
              <span></span>
              <span></span>
              <span className="typing-word">{LOADING_WORDS[loadingWordIndex]}</span>
            </div>
          ) : hasWidget ? (
            <WidgetRenderer
              widgetType={message.widgetType}
              widgetData={message.widgetData}
              widgetActionUserId={message.widgetActionUserId}
              currentUserId={currentUserId}
              messageId={message.id}
              conversationId={message.conversationId}
              hasAssistantFollowUp={hasAssistantFollowUp}
              bulkFollowUp={bulkFollowUp}
              onConfirmationSubmitted={onConfirmationSubmitted}
            />
          ) : isEditing ? (
            <form
              className="message-bubble-edit-form"
              onSubmit={async (e) => {
                e.preventDefault();
                if (editing) return;
                const next = draftContent.trim();
                if (!next || next === (message.content || '').trim()) {
                  setIsEditing(false);
                  return;
                }
                setEditing(true);
                try {
                  await onEditMessage(message.id, message.conversationId, next);
                  setIsEditing(false);
                } catch (err) {
                  // Surface failure to the user; keep the textarea open so
                  // they can retry without losing their draft.
                  // eslint-disable-next-line no-alert
                  window.alert(`Failed to save edit: ${err?.message || 'unknown error'}`);
                } finally {
                  setEditing(false);
                }
              }}
            >
              <textarea
                className="message-bubble-edit-textarea"
                value={draftContent}
                onChange={(e) => setDraftContent(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    e.preventDefault();
                    setIsEditing(false);
                  } else if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    e.target.form?.requestSubmit();
                  }
                }}
                rows={Math.min(8, Math.max(2, draftContent.split('\n').length))}
                autoFocus
                disabled={editing}
              />
              <div ref={editActionsRef} className="message-bubble-edit-actions">
                <button type="button" onClick={() => setIsEditing(false)} disabled={editing}>
                  Cancel
                </button>
                <button type="submit" disabled={editing || !draftContent.trim()}>
                  {editing ? 'Saving…' : 'Save'}
                </button>
              </div>
            </form>
          ) : isDeleted ? (
            <span className="message-bubble-deleted" aria-label="Deleted message">
              This message was deleted
            </span>
          ) : (
            <>
              {hasTextContent && <MarkdownRenderer content={message.content} />}
              {isEdited && <span className="message-bubble-edited-tag" title={`Edited ${new Date(editedAtMs).toLocaleString()}`}>(edited)</span>}
              <MessageAttachments attachments={message.attachments} />
            </>
          )}
        </div>

        {!isDeleted && (
          <ReactionBar
            reactionCounts={reactionCounts}
            messageId={message.id}
            conversationId={message.conversationId}
            currentUserId={currentUserId}
            onReactionCountsChange={setReactionCounts}
            onAddReaction={onAddReaction}
            onToggleReaction={onToggleReaction}
          />
        )}

        {!isDeleted && threadReplyCount > 0 && (
          <button
            type="button"
            className="message-bubble-thread-link"
            onClick={() => onOpenThread && onOpenThread(message.id)}
          >
            {threadReplyCount} {threadReplyCount === 1 ? 'reply' : 'replies'}
            {message.threadLastReplyAt && (
              <span className="thread-link-time">
                {' '}— last reply {relativeTime(message.threadLastReplyAt)}
              </span>
            )}
          </button>
        )}

        {toolbarVisible && !isDeleted && (
          <div className="message-bubble-hover-actions">
            <MessageHoverToolbar
              canEdit={canEdit && !isEditing}
              canDelete={canDelete}
              canForward={canForward}
              canPin={canPin}
              isPinned={isPinned}
              canBookmark={canBookmark}
              isBookmarked={isBookmarked}
              messageText={isDeleted ? '' : (message.content || '')}
              quickReactTokens={quickReactTokens}
              onQuickReact={handleQuickReact}
              onOpenPicker={handleOpenPicker}
              onOpenThread={onOpenThread ? () => onOpenThread(message.id) : undefined}
              onEdit={handleStartEdit}
              onForward={handleForward}
              onPin={handlePin}
              onUnpin={handleUnpin}
              onBookmark={handleBookmark}
              onUnbookmark={handleUnbookmark}
              onDelete={handleDelete}
              onCopyLink={handleCopyLink}
              onDismiss={dismissToolbar}
            />
            {showPicker && (
              <ReactionPicker
                messageId={message.id}
                conversationId={message.conversationId}
                onSelect={(emoji) => {
                  incrementReactionCount(emoji);
                  if (onAddReaction) onAddReaction(message.id, emoji);
                  handleClosePicker();
                }}
                onClose={handleClosePicker}
              />
            )}
            {linkCopiedAt > 0 && (
              <span className="message-hover-toolbar-copied-toast" role="status" aria-live="polite">
                Link copied
              </span>
            )}
          </div>
        )}
      </div>
      {showDeleteConfirm && (
        <DeleteMessageModal
          message={message}
          onClose={cancelDelete}
          onConfirm={confirmDelete}
          returnFocusRef={deleteReturnFocusRef}
        />
      )}
    </div>
  );
}

export default MessageBubble;
